import { Check, Copy } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useEffect, useRef, useState } from "react";
import { Input, TextArea, TextField } from "react-aria-components";
import { cx } from "@/lib/cx";
import { Button } from "./Button";
import { FieldDescription, FieldLabel, fieldRoot, inputBase } from "./field";
import { IconButton } from "./IconButton";

/** Writes to the clipboard; falls back to a hidden textarea where the async API needs a secure context. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Button that copies `value`; the label turns into "Copied" for 2 seconds (announced politely). `value` may be a
 * function that fetches the text on press (a secret that is not kept in the page); if it or the clipboard fails,
 * `onError` is called and nothing is announced as copied. `iconOnly` renders a compact icon button for table rows
 * and needs `label` as its accessible name.
 */
export function CopyButton({
  value,
  label,
  size = "md",
  variant = "secondary",
  iconOnly,
  onError,
  className,
}: {
  value: string | (() => Promise<string>);
  /** Accessible context, e.g. "Invite link"; the visible text stays "Copy". With `iconOnly` it is the whole name. */
  label?: string;
  size?: "sm" | "md";
  variant?: "secondary" | "ghost";
  iconOnly?: boolean;
  onError?: (error: unknown) => void;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const press = async () => {
    let ok = false;
    try {
      ok = await copyText(typeof value === "function" ? await value() : value);
    } catch (e) {
      onError?.(e);
      return;
    }
    if (!ok) {
      onError?.(new Error("clipboard"));
      return;
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {iconOnly ? (
        <IconButton
          aria-label={label ?? m.ui_copy()}
          icon={copied ? Check : Copy}
          iconWeight={copied ? "bold" : undefined}
          size={size}
          variant={variant === "secondary" ? "ghost" : variant}
          className={cx(className, copied && "text-success-text")}
          onPress={() => void press()}
        />
      ) : (
        <Button
          size={size}
          variant={variant}
          className={className}
          aria-label={label ? `${m.ui_copy()}: ${label}` : undefined}
          onPress={() => void press()}
        >
          {copied ? (
            <Check size={14} weight="bold" aria-hidden="true" className="text-success-text" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
          {copied ? m.ui_copied() : m.ui_copy()}
        </Button>
      )}
      <span role="status" className="sr-only">
        {copied ? m.ui_copied() : ""}
      </span>
    </>
  );
}

/**
 * Read-only value with a Copy button: links, commands, redirect URIs, secrets. It is a real read-only
 * input (focusable, selects everything on focus, announced with its label), set in the mono face.
 * `multiline` renders a textarea for commands and snippets that should wrap.
 *
 *   <CopyField label="Invite link" value={link} description="Valid for 7 days." />
 *   <CopyField label="Command" value={cmd} multiline rows={3} />
 */
export function CopyField({
  label,
  value,
  description,
  multiline,
  rows = 3,
  className,
}: {
  label: string;
  value: string;
  description?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
}) {
  return (
    <TextField isReadOnly value={value} className={cx(fieldRoot, className)}>
      <FieldLabel>{label}</FieldLabel>
      <div className={cx("flex gap-2", multiline ? "items-start" : "items-center")}>
        {multiline ? (
          <TextArea
            rows={rows}
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
            className={cx(inputBase, "min-w-0 flex-1 resize-none bg-subtle py-1.5 font-mono text-xs leading-5")}
          />
        ) : (
          <Input
            spellCheck={false}
            onFocus={(e) => e.currentTarget.select()}
            className={cx(inputBase, "h-(--input-h) min-w-0 flex-1 bg-subtle font-mono text-xs")}
          />
        )}
        <CopyButton value={value} label={label} />
      </div>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
    </TextField>
  );
}
